import { useMemo } from "react";

import type { QuoteBoardItem } from "~/shared/schemas/quote-board-schema.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";

import { transformQuoteBoardData } from "../../utils/quote-board-data-transformers.ts";

// Type for transformed data
type TransformedQuoteBoardItem = ReturnType<typeof transformQuoteBoardData>[0];

/**
 * Hook to transform and prepare grid data
 */
export const useTransformedGridData = (
	gridData: QuoteBoardItem[],
	dataRefreshKey: number,
	isInternal = false,
) => {
	return useMemo(() => {
		if (!Array.isArray(gridData)) {
			return [];
		}

		const transformed = transformQuoteBoardData(gridData, isInternal);

		if (dataRefreshKey > 0) {
			// Create deep clone to break any possible reference connections
			return transformed.map((item, index) => ({
				...item,
				// Add a unique marker to force recognition as new data
				_refreshMarker: `${dataRefreshKey}-${index}`,
			}));
		}

		return transformed;
	}, [gridData, dataRefreshKey, isInternal]);
};

/**
 * Hook to generate stable grid key for remounting
 */
export const useGridKey = (
	transformedGridData: TransformedQuoteBoardItem[],
	processedColumns: BoardColumn[],
	dataRefreshKey: number,
	isLoading: boolean,
) => {
	return useMemo(() => {
		const dataHash = transformedGridData.map((item) => item.quoteId).join("-");
		const columnHash = Array.isArray(processedColumns)
			? processedColumns.map((col) => col?.field || "").join("-")
			: "";
		return `quote-board-grid-${dataHash.slice(0, 10)}-${columnHash.slice(0, 10)}-${dataRefreshKey}-${isLoading}`;
	}, [transformedGridData, processedColumns, dataRefreshKey, isLoading]);
};
